import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";

import { SearchBoxCore, SessionToken } from "@mapbox/search-js-core";
import { Field } from "formik";

import debounce from "lodash/debounce";

import { Input } from "shared/components/Form";
import {
  getAddressFromMapboxFeature,
  getUserLocation,
} from "shared/utils/addressMapbox";

type FieldMapping = {
  address: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

type AddressAutocompleteInputMapboxProps = {
  index: number;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  error: any;
  id: string;
  name: string;
  fieldMapping: FieldMapping;
  onBlur: () => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  trimOnBlur?: boolean;
};

const AddressAutocompleteInputMapbox: React.FC<AddressAutocompleteInputMapboxProps> =
  ({
    index,
    setFieldValue,
    error,
    id,
    name,
    fieldMapping,
    onBlur,
    disabled = false,
    label = "Address",
    required = true,
    trimOnBlur = true,
  }) => {
    const inputRef = useRef<any>(null);
    const searchBoxRef = useRef<SearchBoxCore | null>(null);
    const sessionTokenRef = useRef<SessionToken | null>(null);
    const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [userLocation, setUserLocation] = useState<{
      longitude?: number;
      latitude?: number;
    }>({});

    // Initialize MapBox SearchBox
    useEffect(() => {
      const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      if (!accessToken) {
        console.error("MapBox access token not found");
        return;
      }

      try {
        searchBoxRef.current = new SearchBoxCore({ accessToken });
        sessionTokenRef.current = new SessionToken();
      } catch (error) {
        console.error("Error initializing MapBox:", error);
      }

      // Get user location for proximity bias (non-blocking)
      getUserLocation().then((location) => {
        if (location.longitude && location.latitude) {
          setUserLocation(location);
        }
      });

      return () => {
        searchBoxRef.current = null;
        sessionTokenRef.current = null;
      };
    }, []);

    const setFormikFields = useCallback(
      (addressDetails: any) => {
        Object.entries(addressDetails).forEach(([key, value]) => {
          if (fieldMapping[key as keyof FieldMapping]) {
            setFieldValue(fieldMapping[key as keyof FieldMapping], value);
          }
        });
      },
      [fieldMapping, setFieldValue]
    );

    const handleSuggestionClick = useCallback(
      (suggestion: any) => {
        const addressDetails = getAddressFromMapboxFeature(suggestion);
        setFormikFields(addressDetails);
        setShowSuggestions(false);
        setSearchSuggestions([]);

        // Set the input value to the selected address
        if (inputRef.current?.input) {
          inputRef.current.input.value = addressDetails.address;
        }

        onBlur();
      },
      [setFormikFields, onBlur]
    );

    // Debounced search function
    const debouncedSearch = useMemo(
      () =>
        debounce(async (query: string) => {
          // Check for minimum 3 non-space characters
          const nonSpaceChars = query.replace(/\s/g, "").length;
          if (!searchBoxRef.current || nonSpaceChars < 3) {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            return;
          }

          try {
            const searchOptions: any = {
              sessionToken: sessionTokenRef.current,
              limit: 5,
              country: "US",
              types: "address",
              language: "en",
            };

            // Use cached location if available (non-blocking)
            if (userLocation?.longitude && userLocation?.latitude) {
              searchOptions.proximity = [
                userLocation.longitude,
                userLocation.latitude,
              ];
            } else {
              // Try to get location in background if we don't have it yet
              // This handles cases where user grants permission after initial denial
              getUserLocation().then((location) => {
                if (location.longitude && location.latitude) {
                  setUserLocation(location);
                }
              });
            }

            const response = await searchBoxRef.current.suggest(
              query,
              searchOptions
            );

            if (response?.suggestions && response.suggestions.length > 0) {
              setSearchSuggestions(response.suggestions);
              setShowSuggestions(true);
            } else {
              setSearchSuggestions([]);
              setShowSuggestions(false);
            }
          } catch (error) {
            console.error("MapBox search error:", error);
            setSearchSuggestions([]);
            setShowSuggestions(false);
          }
        }, 300),
      [userLocation]
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setFieldValue(name, value);
        debouncedSearch(value);
      },
      [name, setFieldValue, debouncedSearch]
    );

    const handleInputBlur = useCallback(() => {
      // Delay hiding suggestions to allow click events to register
      setTimeout(() => {
        setShowSuggestions(false);
        onBlur();
      }, 200);
    }, [onBlur]);

    return (
      <div style={{ position: "relative" }}>
        <Field id={id} name={name}>
          {({ field }) => (
            <>
              <Input
                {...field}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                label={label}
                type="text"
                id={id}
                ref={inputRef}
                placeholder=""
                required={required}
                disabled={disabled}
                error={error}
                formikContext={{
                  trimOnBlur,
                  setFieldValue,
                }}
                autoComplete="off"
              />
              {showSuggestions && searchSuggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {searchSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: "12px",
                        cursor: "pointer",
                        borderBottom:
                          idx < searchSuggestions.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                      }}
                    >
                      <div style={{ fontSize: "14px", color: "#333" }}>
                        {suggestion.address || suggestion.name}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "2px",
                        }}
                      >
                        {suggestion.place_formatted}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Field>
      </div>
    );
  };

export default AddressAutocompleteInputMapbox;
